<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { CategoryType } from "mws-ts-v2-sdk";

import { queries } from "~/queries";
import { useEventQuery } from "~/queries/catalog/events";

definePageMeta({
  ssr: true,
  middleware: "cache-control-server",
});

const route = useRoute();
const eventSlug = computed(() => route.params.eventSlug as string);

const isInAppBrowser = computed(() => getIsInAppBrowser());
const { result: shouldShowOneTap, loaded: growthbookLoaded } = useGrowthbook({
  feature: "account-creation-conversion-event-page-2",
  fallback: false,
  enabled: () => !isInAppBrowser.value,
});

const { data: event, suspense: suspenseEvent } = useEventQuery(eventSlug, {
  redirectNotFound: true,
});

const { data: story, suspense: storySuspense } = useStoryblokQueryWithBridge({
  folderPath: "pages/event",
  slugParamName: "eventSlug",
});

const metaTags = computed(() => story.value?.content?.metaTags);
const queryClient = useQueryClient();

onServerPrefetch(async () => {
  await Promise.all([suspenseEvent(), storySuspense()]);
  // we invalidate the query here to fetch if the user has this as a favourite
  queryClient.invalidateQueries({
    queryKey: queries.events.details._def,
  });
});

const { eventViewed } = useAnalytics();
useTrackEventOnce(event, eventViewed);

const teamId = computed(() => {
  const foundTeam = event.value?.categories.find(
    (category) => category.externalReference?.type === CategoryType.Team,
  );

  return foundTeam?.id ?? null;
});

const userAccountCreationPopupLoaded = useDelayClientLoad(2000);
</script>

<template>
  <div>
    <BaseLoadingSpinner v-if="!event" />
    <div
      v-else
      class="page-container"
    >
      <EventPageHeadTags
        :event="event"
        :meta-tags="metaTags"
      />

      <EventHeroBanner
        :event="event"
        class="browser-viewport-span"
      />

      <template
        v-if="
          story?.content?.centerContent &&
          isStoryblokContentForWeb(story.content.showOn)
        "
      >
        <Blok
          v-for="blok in story?.content.centerContent"
          :key="blok._uid"
          v-editable="blok"
          :blok="blok"
          class="site-viewport-container"
        />
      </template>

      <ProductsOverviewBlock :event-identifier="eventSlug" />

      <template
        v-if="
          story?.content?.bottomContent &&
          isStoryblokContentForWeb(story.content.showOn)
        "
      >
        <Blok
          v-for="blok in story?.content.bottomContent"
          :key="blok._uid"
          v-editable="blok"
          :blok="blok"
          class="site-viewport-container"
        />
      </template>

      <ProductSectionRecentlyViewedProducts />
    </div>

    <LazyUserAccountCreationPopup
      v-if="
        teamId &&
        userAccountCreationPopupLoaded &&
        (isInAppBrowser || (!shouldShowOneTap && growthbookLoaded))
      "
      :team-id="teamId"
      page-type="EVENT"
    />
  </div>
</template>

<style lang="postcss">
.page-container > .block-viewport-container {
  @apply site-viewport-container;
}
</style>
